<template>
  <v-container
    v-if="authenticationMixin.checkedLogin"
    class="content content-with-nav pa-0"
    fluid
  >
    <v-form
      v-model="valid"
      ref="form"
    >
      <!-- Hero section -->
      <page-hero title="Muziek uitvaart" intro-lg="8">
        <p class="page-hero-intro text-center">
          Welke muziek wenst u te horen in de plechtigheid?
          We voorzien vijf muziekstukken: één bij aanvang, drie in het middendeel en één aan het einde.
          U mag een reservelied voorzien wanneer u vermoedt dat er veel getuigenissen zijn.
        </p>
        <p class="page-hero-intro text-center mt-2">
          U kan kiezen uit een uitgebreide collectie, maar u kan ook persoonlijk gekozen muziekfragmenten toevoegen.
          Wij voorzien de muziekbestanden. Enkel bij specifieke uitvoeringen is het raadzaam om zelf de muziek aan te leveren (mp3-formaat, géén Spotify).
          Wijzigingen kunnen tot 24 uur voor de aanvang van de plechtigheid (zaterdag, zondag en feestdagen niet meegerekend).
        </p>
      </page-hero>

      <!-- Two-panel layout -->
      <v-row
        justify="center"
        class="mb-8 pb-8 muziek-columns"
      >
        <!-- Left: Mijn muziekkeuze -->
        <v-col
          class="center px-4"
          cols="12"
          lg="6"
        >
          <div class="muziek-keuze">
            <div class="muziek-keuze-header">
              <v-icon
                color="#907322"
                size="22"
              >
                mdi-playlist-music
              </v-icon>
              <h2 class="muziek-keuze-title">
                Mijn muziekkeuze
              </h2>
            </div>

            <template
              v-for="(sectie, sectieIndex) in secties"
              :key="sectieIndex"
            >
              <div class="muziek-sectie">
                <h3 class="muziek-sectie-titel">
                  {{ sectie.titel }}
                </h3>
                <div
                  v-for="slotIndex in sectie.indices"
                  class="muziek-slot"
                  :key="slotIndex"
                >
                  <template v-if="data.keuze[slotIndex]">
                    <div class="muziek-slot-filled">
                      <div class="muziek-slot-info">
                        <template v-if="data.keuze[slotIndex].idMuziek || data.geblokkeerd">
                          <span class="muziek-slot-type">
                            {{ lists.muziekType.find(x => x.value === data.keuze[slotIndex].muziekType)?.longDescription }}
                          </span>
                          <span class="muziek-slot-titel">{{ data.keuze[slotIndex].titel }}</span>
                          <span class="muziek-slot-artiest">{{ data.keuze[slotIndex].artiest }}</span>
                        </template>
                        <template v-else>
                          <control-select
                            v-model="data.keuze[slotIndex].muziekType"
                            dense
                            :disabled="data.geblokkeerd"
                            itemValue="value"
                            itemText="longDescription"
                            :items="lists.muziekType"
                            label="Type"
                            outlined
                            required
                            :requiredText="textMixin.messages.required"
                          />
                          <v-text-field
                            v-model="data.keuze[slotIndex].titel"
                            base-color="primary"
                            bg-color="white"
                            color="primary"
                            density="compact"
                            :disabled="data.geblokkeerd"
                            label="Titel"
                            :rules="[validationMixin.rules.max50Characters, validationMixin.rules.required]"
                            variant="outlined"
                          />
                          <v-text-field
                            v-model="data.keuze[slotIndex].artiest"
                            base-color="primary"
                            bg-color="white"
                            color="primary"
                            density="compact"
                            :disabled="data.geblokkeerd"
                            label="Artiest"
                            :rules="[validationMixin.rules.max50Characters, validationMixin.rules.required]"
                            variant="outlined"
                          />
                        </template>
                      </div>
                      <v-btn
                        color="primary"
                        :disabled="data.geblokkeerd"
                        icon
                        size="x-small"
                        variant="text"
                        @click="RemoveFromSlot(slotIndex)"
                      >
                        <v-icon size="18">
                          mdi-close
                        </v-icon>
                      </v-btn>
                    </div>
                  </template>
                  <div
                    v-else
                    class="muziek-slot-empty"
                  >
                    <span class="muziek-slot-empty-text">Geen nummer geselecteerd.</span>
                    <v-btn
                      v-if="!data.geblokkeerd"
                      color="primary"
                      size="small"
                      variant="text"
                      @click="MuziekEigenNummerToevoegen(slotIndex)"
                    >
                      + Eigen nummer toevoegen
                    </v-btn>
                  </div>
                </div>
              </div>
            </template>
          </div>

          <v-textarea
            v-model="data.opmerking"
            auto-grow
            base-color="primary"
            bg-color="white"
            class="mt-4"
            color="primary"
            density="compact"
            :disabled="data.geblokkeerd"
            label="Opmerking"
            placeholder="Plaats hier eventuele extra opmerkingen: bv. wanneer een liedje op een welbepaald moment in de uitvaart aan bod mag komen."
            variant="outlined"
          />
          <div class="d-flex justify-end">
            <v-btn
              v-if="!data.geblokkeerd"
              color="primary"
              :disabled="data.geblokkeerd"
              rounded
              @click="Save"
            >
              <v-icon
                class="mr-1"
                size="18"
              >
                mdi-content-save-outline
              </v-icon>
              Opslaan
            </v-btn>
          </div>
        </v-col>

        <!-- Right: Collectie -->
        <v-col
          class="center px-4"
          cols="12"
          lg="4"
        >
          <div class="muziek-collectie">
            <div class="muziek-collectie-header">
              <v-icon
                color="#424E58"
                size="22"
              >
                mdi-music-note
              </v-icon>
              <h2 class="muziek-collectie-title">
                Collectie Verstappen
              </h2>
            </div>

            <!-- Type filters -->
            <div class="muziek-filters">
              <v-btn
                v-for="(muziekType, index) in lists.muziekType"
                :color="selectedMuziekType.includes(muziekType.value) ? 'secondary' : '#C6C9CC'"
                :key="index"
                rounded
                size="small"
                @click="SelectMuziekType(muziekType.value)"
              >
                {{ muziekType.longDescription }}
              </v-btn>
            </div>

            <v-text-field
              v-model="search"
              append-inner-icon="mdi-magnify"
              base-color="primary"
              clearable
              :color="Muziek.length === 0 && search ? 'error' : 'primary'"
              density="compact"
              label="Zoeken"
              variant="outlined"
            />

            <!-- Audio player -->
            <div
              v-if="current.idMuziek"
              class="muziek-player"
            >
              <control-music
                :modelValue="current.src"
                :title="Title(current)"
              />
            </div>

            <!-- Empty state -->
            <p
              v-if="PageList.length === 0"
              class="muziek-empty"
            >
              Er is momenteel geen muziek die binnen uw zoekcriteria valt.
            </p>

            <!-- Pagination top -->
            <v-pagination
              v-if="PageCount > 1"
              v-model="page"
              class="mt-3"
              color="primary"
              :length="PageCount"
            />

            <!-- Song list -->
            <div class="muziek-lijst">
              <div
                v-for="(lied, index) in PageList"
                class="muziek-lied"
                :class="{ 'muziek-lied--active': current === lied, 'muziek-lied--alt': index % 2 === 0 }"
                :key="index"
              >
                <v-icon
                  class="muziek-lied-play"
                  color="#907322"
                  @click="SelectCurrent(lied)"
                >
                  mdi-{{ current === lied ? 'volume-low' : 'play' }}
                </v-icon>
                <div class="muziek-lied-info">
                  <span class="muziek-lied-titel">{{ lied.titel }}</span>
                  <span class="muziek-lied-artiest">{{ lied.artiest }}</span>
                  <v-select
                    class="mt-1"
                    clearable
                    color="primary"
                    density="compact"
                    :disabled="data.geblokkeerd"
                    hide-details
                    item-title="longDescription"
                    item-value="value"
                    :items="slotOpties"
                    :modelValue="GetSlotForSong(lied)"
                    placeholder="Plaats in..."
                    variant="outlined"
                    @update:modelValue="PlaceSongInSlot(lied, $event)"
                  />
                </div>
              </div>
            </div>

            <!-- Pagination bottom -->
            <v-pagination
              v-if="PageCount > 1"
              v-model="page"
              color="primary"
              :length="PageCount"
            />
          </div>
        </v-col>
      </v-row>
    </v-form>

    <!-- Navigation -->
    <div class="samenstellen-nav">
      <div class="samenstellen-nav-inner">
        <v-btn
          v-if="samenstellenNavigation_PreviousItem"
          color="primary"
          :disabled="samenstellenNavigation_PreviousItem.disabled"
          rounded
          variant="text"
          @click="samenstellenNavigation_GoToPrevious"
        >
          <v-icon start>
            mdi-chevron-left
          </v-icon>
          {{ samenstellenNavigation_PreviousItem.title }}
        </v-btn>
        <span v-else />
        <v-btn
          v-if="samenstellenNavigation_NextItem"
          color="primary"
          :disabled="samenstellenNavigation_NextItem.disabled"
          rounded
          variant="text"
          @click="samenstellenNavigation_GoToNext"
        >
          {{ samenstellenNavigation_NextItem.title }}
          <v-icon end>
            mdi-chevron-right
          </v-icon>
        </v-btn>
        <span v-else />
      </div>
    </div>
  </v-container>
</template>

<script>
import PageHero from '@/components/PageHero.vue'
import authenticationMixin from '@/mixins/authenticationMixin'
import commonMixin from '@/mixins/commonMixin'
import constantMixin from '@/mixins/constantMixin'
import errorMixin from '@/mixins/errorMixin'
import listMixin from '@/mixins/listMixin'
import samenstellenNavigationMixin from '@/mixins/samenstellenNavigationMixin'
import validationMixin from '@/mixins/validationMixin'
import blobService from '@/services/blobService'
import klantService from '@/services/klantService'
import ControlMusic from '../ControlMusic.vue'
import ControlSelect from '../ControlSelect.vue'

export default {
  name: 'MuziekComponent',
  mixins: [
    authenticationMixin,
    commonMixin,
    constantMixin,
    errorMixin,
    listMixin,
    samenstellenNavigationMixin,
    validationMixin
  ],
  components: {
    ControlMusic,
    ControlSelect,
    PageHero
  },
  data () {
    return {
      current: {},
      data: {
        geblokkeerd: false,
        keuze: [],
        opmerking: null
      },
      lists: {
        code: [],
        muziekType: []
      },
      maxKeuze: 6,
      numberPerPage: 20,
      page: 1,
      search: '',
      secties: [
        { titel: 'Bij aanvang van de plechtigheid:', indices: [0] },
        { titel: 'Middendeel:', indices: [1, 2, 3] },
        { titel: 'Aan het einde van de plechtigheid:', indices: [4] },
        { titel: 'Reservelied:', indices: [5] }
      ],
      selectedMuziekType: [],
      slotOpties: [],
      valid: false
    }
  },
  mounted () {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    this.AuthenticationMixin_CheckIfLoggedIn()
    this.$store.state.headers.samenstellen = true
    blobService.Init(this)
    klantService.Init(this)
    this.GetUitvaartMuziek()
  },
  beforeUnmount () {
    this.$store.state.headers.samenstellen = false
  },
  watch: {
    PageCount (value) {
      if (this.page > value) {
        this.page = value
      }
    }
  },
  computed: {
    Muziek () {
      let list = this.listMixin.list
      list = list.filter(x => this.selectedMuziekType.includes(x.muziekType))

      if (this.search) {
        list = list.filter(x => x.artiest.toLowerCase().includes(this.search.toLowerCase()) || x.titel.toLowerCase().includes(this.search.toLowerCase()))
      }

      return list
    },
    PageList () {
      return this.CommonMixin_GetPageList(this.Muziek, this.page, this.numberPerPage)
    },
    PageCount () {
      return this.CommonMixin_GetPageCount(this.Muziek, this.numberPerPage)
    }
  },
  methods: {
    async GetBlobMuziek (idBlob) {
      this.CommonMixin_Load(this.$options.name, true)
      const resp = await blobService.GetBase64(idBlob)
      this.CommonMixin_Load(this.$options.name, false)

      if (resp && (resp.data || resp.data !== '')) {
        return this.CommonMixin_Clone(resp.data)
      } else {
        return null
      }
    },
    GetSlotForSong (lied) {
      const index = this.data.keuze.findIndex(x => x && x.idMuziek === lied.idMuziek)
      return index !== -1 ? index : null
    },
    async GetUitvaartMuziek () {
      this.CommonMixin_Load(this.$options.name, true)
      const resp = await klantService.GetUitvaartMuziek()

      if (resp && (resp.data || resp.data !== '')) {
        this.data = this.CommonMixin_Clone(resp.data)
        this.RestoreKeuzeSlots()
      }

      await this.GetUitvaartMuziekLists()
      this.CommonMixin_Load(this.$options.name, false)
    },
    async GetUitvaartMuziekListCode () {
      const resp = await klantService.GetUitvaartMuziekListCode()

      if (resp?.data) {
        this.lists.code = resp.data
        this.lists.muziekType = this.CommonMixin_GetElementsByPropertyValue(resp.data, 'type', this.constantMixin.codes.muziekType.name)
        this.selectedMuziekType = this.lists.muziekType.map(x => x.value)
      }
    },
    async GetMuziekSlotOpties () {
      const resp = await klantService.GetUitvaartDraaiboekListCode()

      if (resp?.data) {
        this.slotOpties = resp.data.filter(o => o.type === this.constantMixin.codes.muziekSlot.name)
      }
    },
    async GetUitvaartMuziekListMuziek () {
      this.CommonMixin_Load(this.$options.name, true)
      const resp = await klantService.GetUitvaartMuziekListMuziek()

      if (resp && (resp.data || resp.data !== '')) {
        this.listMixin.list = resp.data
      }

      this.CommonMixin_Load(this.$options.name, false)
    },
    async GetUitvaartMuziekLists () {
      await this.GetUitvaartMuziekListMuziek()
      await this.GetUitvaartMuziekListCode()
      await this.GetMuziekSlotOpties()
    },
    MuziekEigenNummerToevoegen (slotIndex) {
      this.data.keuze.splice(slotIndex, 1, {
        artiest: '',
        titel: ''
      })
    },
    PlaceSongInSlot (lied, slotIndex) {
      const currentSlot = this.data.keuze.findIndex(x => x && x.idMuziek === lied.idMuziek)
      if (currentSlot !== -1) {
        this.data.keuze.splice(currentSlot, 1, null)
      }
      if (slotIndex !== null && slotIndex !== undefined) {
        const liedClone = this.CommonMixin_Clone(lied)
        this.data.keuze.splice(slotIndex, 1, liedClone)
      }
    },
    RemoveFromSlot (slotIndex) {
      this.data.keuze.splice(slotIndex, 1, null)
    },
    RestoreKeuzeSlots () {
      const slots = Array(this.maxKeuze).fill(null)
      this.data.keuze.forEach((item, index) => {
        if (item) {
          const slot = item.volgorde != null ? item.volgorde : index
          if (slot >= 0 && slot < this.maxKeuze) {
            slots[slot] = item
          }
        }
      })
      this.data.keuze = slots
    },
    async Save () {
      this.$refs.form.validate()

      if (this.valid) {
        this.CommonMixin_Load(this.$options.name, true)
        const saveData = this.CommonMixin_Clone(this.data)
        saveData.keuze = saveData.keuze
          .map((x, index) => x !== null ? { ...x, volgorde: index } : null)
          .filter(x => x !== null)
        const resp = await klantService.SaveUitvaartMuziek(saveData)

        if (resp && (resp.data || resp.data !== '')) {
          this.CommonMixin_Snackbar(this.textMixin.messages.saved, 'success')
        }

        this.CommonMixin_Load(this.$options.name, false)
      } else {
        this.CommonMixin_Snackbar(this.textMixin.messages.formInvalid, 'error')
      }
    },
    async SelectCurrent (lied) {
      if (!lied.loaded) {
        if (lied.idBlobMuziek) {
          lied.src = (await this.GetBlobMuziek(lied.idBlobMuziek))?.blobDataString ?? lied.src
        }

        lied.loaded = true
      }

      this.current = this.current === lied ? {} : lied
    },
    SelectMuziekType (value) {
      if (this.selectedMuziekType.includes(value)) {
        this.selectedMuziekType = this.selectedMuziekType.filter(x => x !== value)
      } else {
        this.selectedMuziekType.push(value)
      }
    },
    Title (lied) {
      return `${lied.titel}${lied.titel && lied.artiest ? ' - ' : ''}${lied.artiest}`
    }
  }
}
</script>

<style scoped>
.muziek-columns {
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
}

/* Mijn muziekkeuze panel */
.muziek-keuze {
  border: 1px solid #e8e4dc;
  border-radius: 10px;
  padding: 24px;
  background-color: #fdfcfa;
}

.muziek-keuze-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-bottom: 2px solid #DEC067;
}

.muziek-keuze-title {
  font-size: 20px;
  font-weight: 500;
  color: #424E58;
  padding: 0;
  margin: 0;
}

.muziek-sectie {
  margin-bottom: 12px;
}

.muziek-sectie-titel {
  font-size: 14px;
  font-weight: 600;
  color: #907322;
  margin: 0 0 8px 0;
  padding: 0;
}

.muziek-slot {
  margin-bottom: 8px;
  padding: 10px 12px;
  border: 1px solid #e8e4dc;
  border-radius: 8px;
  background-color: white;
}

.muziek-slot-filled {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.muziek-slot-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.muziek-slot-type {
  font-size: 12px;
  font-style: italic;
  color: #999;
}

.muziek-slot-titel {
  font-size: 14px;
  font-weight: 500;
  color: #424E58;
}

.muziek-slot-artiest {
  font-size: 13px;
  color: #6b6b6b;
}

.muziek-slot-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 0;
}

.muziek-slot-empty-text {
  font-size: 13px;
  font-style: italic;
  color: #999;
}

/* Collectie panel */
.muziek-collectie-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-bottom: 2px solid #424E58;
}

.muziek-collectie-title {
  font-size: 20px;
  font-weight: 500;
  color: #424E58;
  padding: 0;
  margin: 0;
}

.muziek-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
}

.muziek-player {
  margin-bottom: 12px;
}

.muziek-empty {
  font-size: 14px;
  font-style: italic;
  color: #999;
  text-align: center;
  padding: 32px 0;
}

/* Song list */
.muziek-lijst {
  margin: 12px 0;
}

.muziek-lied {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  transition: background-color 0.2s ease;
}

.muziek-lied--alt {
  background-color: #fdfcfa;
}

.muziek-lied--active {
  background-color: #FBF7EE;
}

.muziek-lied-play {
  flex-shrink: 0;
  cursor: pointer;
  margin-top: 2px;
}

.muziek-lied-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.muziek-lied-titel {
  font-size: 14px;
  font-weight: 500;
  color: #424E58;
}

.muziek-lied-artiest {
  font-size: 13px;
  font-style: italic;
  color: #6b6b6b;
}
</style>
